import * as v from 'valibot'

import type { FirestoreDocumentSnapshot } from '~shared/core/firestore'
import type { Runner } from '~shared/types'

import type { SubTimer } from './cue'

// Running state of sub timers. The settings live on the cue (`cue.subTimers`);
// the running state lives on the runner, one doc per timer in
// `runners/{runnerId}/sub-timers/{timerId}`, written by the server only.
//
// Every time here is server epoch ms. Clients add their measured offset to
// `Date.now()` before calling these functions.

export const SUB_TIMER_RUN_COLLECTION = 'sub-timers'

// =============== FIRESTORE BOUNDARY

// The same anchors as the active cue: elapsed is `(pausedAt ?? now) - startedAt`.
// Both null is the reset state. A resume slides `startedAt` forward by the pause.
export const SubTimerRunFirestoreSchema = v.object({
  cueId: v.string(),
  /** Effective start, or null when reset. */
  startedAt: v.nullish(v.number(), null),
  /** When it was paused, or null while running or reset. */
  pausedAt: v.nullish(v.number(), null),
})

export type SubTimerRun = v.InferOutput<typeof SubTimerRunFirestoreSchema>

/** The time fields of a run, as the actions produce them. */
export type SubTimerRunTimes = Omit<SubTimerRun, 'cueId'>

/** A timer's manual running state. Absent until someone takes over. */
export type SubTimerRunWithId = SubTimerRun & { id: string }

/**
 * @throws {Error} naming the doc and every issue when the body does not match.
 */
export function parseSubTimerRunSnapshot(snapshot: FirestoreDocumentSnapshot): SubTimerRunWithId {
  const result = v.safeParse(SubTimerRunFirestoreSchema, snapshot.data() ?? {})
  if (!result.success) throw new Error(`Invalid sub timer run ${snapshot.id}:\n${v.summarize(result.issues)}`)
  return { ...result.output, id: snapshot.id }
}

// =============== CUE RUN

/** The part of a cue's run that auto-start timers follow. */
export type SubTimerCueRun =
  | { state: 'active'; running: boolean; kickoff: number; lastStop: number }
  | { state: 'past'; startTime: number; duration: number }

/**
 * Where a cue stands on the runner, for auto-start timers. Shared by client
 * and server so both resolve a timer the same way.
 */
export function getSubTimerCueRun(runner: Runner | null, cueId: string): SubTimerCueRun | null {
  if (!runner) return null
  const { timesnap, elapsedCues } = runner
  if (timesnap.cueId === cueId)
    return { state: 'active', running: timesnap.running, kickoff: timesnap.kickoff, lastStop: timesnap.lastStop }
  const elapsed = elapsedCues?.[cueId]
  if (elapsed) return { state: 'past', startTime: new Date(elapsed.startTime).getTime(), duration: elapsed.duration }
  return null
}

export function isSameSubTimerCueRun(a: SubTimerCueRun | null, b: SubTimerCueRun | null): boolean {
  if (a === b) return true
  if (!a || !b || a.state !== b.state) return false
  if (a.state === 'past') return a.startTime === (b as typeof a).startTime && a.duration === (b as typeof a).duration
  const other = b as typeof a
  return a.running === other.running && a.kickoff === other.kickoff && a.lastStop === other.lastStop
}

// =============== RESOLVED STATE

/** Settings plus the running state they resolve to right now. */
export type SubTimerState = SubTimer & {
  startedAt: number | null
  pausedAt: number | null
  /** Following the cue: auto-start with no manual run yet. */
  armed: boolean
}

/**
 * The running state a timer shows. A manual run wins; otherwise an auto-start
 * timer mirrors its cue: running with it, paused with it, frozen once past.
 */
export function resolveSubTimer(
  timer: SubTimer,
  run: SubTimerRun | null,
  cueRun: SubTimerCueRun | null,
): SubTimerState {
  if (run) return { ...timer, startedAt: run.startedAt, pausedAt: run.pausedAt, armed: false }
  if (!timer.autoStart) return { ...timer, startedAt: null, pausedAt: null, armed: false }
  if (cueRun?.state === 'active')
    return { ...timer, startedAt: cueRun.kickoff, pausedAt: cueRun.running ? null : cueRun.lastStop, armed: true }
  if (cueRun?.state === 'past')
    return { ...timer, startedAt: cueRun.startTime, pausedAt: cueRun.startTime + cueRun.duration, armed: true }
  return { ...timer, startedAt: null, pausedAt: null, armed: true }
}

/** What the clock math reads: the settings that shape the count, plus the run anchors. */
export type SubTimerClock = Pick<SubTimerState, 'direction' | 'duration' | 'endAction' | 'startedAt' | 'pausedAt'>

function getRawElapsed(timer: SubTimerClock, now: number): number {
  return timer.startedAt === null ? 0 : (timer.pausedAt ?? now) - timer.startedAt
}

function stopsAtZero(timer: SubTimerClock): boolean {
  return timer.direction === 'down' && timer.endAction === 'stop'
}

export function getSubTimerElapsed(timer: SubTimerClock, now: number): number {
  const elapsed = getRawElapsed(timer, now)
  return stopsAtZero(timer) ? Math.min(elapsed, timer.duration) : elapsed
}

/** A stop-at-zero countdown that has reached 00:00. */
export function isSubTimerFinished(timer: SubTimerClock, now: number): boolean {
  return stopsAtZero(timer) && getRawElapsed(timer, now) >= timer.duration
}

/** Remaining time for a countdown (negative once over), elapsed for a count up. */
export function getSubTimerDisplayMs(timer: SubTimerClock, now: number): number {
  const elapsed = getSubTimerElapsed(timer, now)
  return timer.direction === 'down' ? timer.duration - elapsed : elapsed
}

// =============== CUES

/**
 * Every sub timer of one cue, resolved to its current state. A run recorded for
 * another cue (a copied timer that kept its id) is ignored, so the copy never
 * shows the original's state.
 */
export function resolveCueSubTimers(
  cue: { id: string; subTimers: readonly SubTimer[] },
  runs: Readonly<Record<string, SubTimerRun | undefined>>,
  runner: Runner | null,
): SubTimerState[] {
  if (!cue.subTimers.length) return []
  const cueRun = getSubTimerCueRun(runner, cue.id)
  return cue.subTimers.map((timer) => {
    const run = runs[timer.id]
    return resolveSubTimer(timer, run?.cueId === cue.id ? run : null, cueRun)
  })
}

// =============== OUTPUT

/**
 * The sub timers an output shows in place of the cue countdown: every one set
 * to show on output, in cue order then list order, whatever its state
 * (running, paused, idle, finished or over).
 *
 * @param cues In rundown order.
 */
export function selectOutputSubTimers(
  cues: Iterable<{ id: string; subTimers: readonly SubTimer[] }>,
  runs: Readonly<Record<string, SubTimerRun | undefined>>,
  runner: Runner | null,
): SubTimerState[] {
  const shown: SubTimerState[] = []
  for (const cue of cues) {
    if (!cue.subTimers.some((timer) => timer.showOnOutput)) continue
    shown.push(...resolveCueSubTimers(cue, runs, runner).filter((timer) => timer.showOnOutput))
  }
  return shown
}

// =============== ACTIONS
//
// Each takes the resolved state and returns the manual run to store.

/** Pins a countdown that already hit 00:00 at 00:00, so later actions start from there. */
function settle(timer: SubTimerState, now: number): SubTimerRunTimes {
  const { startedAt, pausedAt } = timer
  if (startedAt !== null && pausedAt === null && isSubTimerFinished(timer, now))
    return { startedAt, pausedAt: startedAt + timer.duration }
  return { startedAt, pausedAt }
}

/** Counting now: started, not paused, and not a stop-at-zero countdown that hit 00:00. */
export function isSubTimerRunning(timer: SubTimerClock, now: number): boolean {
  return timer.startedAt !== null && timer.pausedAt === null && !isSubTimerFinished(timer, now)
}

/** Running or paused: started since its last reset. False while idle or reset. */
export function isSubTimerStarted(timer: SubTimerClock): boolean {
  return timer.startedAt !== null
}

export function toggleSubTimerRun(timer: SubTimerState, now: number): SubTimerRunTimes {
  const { startedAt, pausedAt } = settle(timer, now)
  if (startedAt === null) return { startedAt: now, pausedAt: null }
  if (pausedAt === null) return { startedAt, pausedAt: now }
  // Resume: slide the start forward by the time spent paused
  return { startedAt: startedAt + (now - pausedAt), pausedAt: null }
}

/**
 * Stop and go back to the full time. Stored as a run, so an auto-start timer
 * stays stopped rather than falling back to its cue's elapsed time.
 */
export function resetSubTimerTimes(): SubTimerRunTimes {
  return { startedAt: null, pausedAt: null }
}

/** Start, or leave running. Safe to repeat. */
export function startSubTimerRun(timer: SubTimerState, now: number): SubTimerRunTimes {
  if (isSubTimerRunning(timer, now)) return { startedAt: timer.startedAt, pausedAt: timer.pausedAt }
  return toggleSubTimerRun(timer, now)
}

/** Pause, or leave stopped. Safe to repeat. A finished countdown stays pinned at 00:00. */
export function pauseSubTimerRun(timer: SubTimerState, now: number): SubTimerRunTimes {
  if (isSubTimerRunning(timer, now)) return toggleSubTimerRun(timer, now)
  return settle(timer, now)
}
